import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { Trophy, LogIn, Medal } from "lucide-react";
import { api } from "@/lib/api";
import { authHeader } from "@/lib/aadhaar";
import { isStudent } from "@/lib/classroom_live";
import { useLang } from "@/context/LanguageContext";

const MEDAL = ["text-[#D4A017]", "text-slate-400", "text-[#B87333]"];
const day = (d) => format(new Date(`${d}T00:00:00`), "d MMM");

const Row = ({ r, i }) => {
  const { L } = useLang();
  return (
    <li className={`flex items-center gap-3 rounded-2xl px-3 py-2.5 ${r.me ? "bg-[#E0FAFF] border border-[#00B8D4]/50" : "bg-white border border-slate-200"}`} data-testid={`leaderboard-row-${i}`}>
      <span className="w-8 text-center font-black text-[#0A192F]" data-testid={`leaderboard-rank-${i}`}>{r.rank && r.rank <= 3 ? <Medal className={`w-5 h-5 mx-auto ${MEDAL[r.rank - 1]}`} /> : r.rank || "—"}</span>
      <span className="flex-1 min-w-0"><span className="block text-sm font-bold text-[#0A192F] truncate">{r.name}{r.me ? L(" (you)", " (आप)") : ""}</span>
        <span className="block text-[11px] text-slate-500">{L(`Class ${r.class_grade} · ${r.correct} correct · ${r.days} day(s)`, `कक्षा ${r.class_grade} · ${r.correct} सही · ${r.days} दिन`)}</span></span>
      <span className="font-black text-[#007A8A]" data-testid={`leaderboard-points-${i}`}>{r.points}</span>
    </li>
  );
};

// Weekly school leaderboard for the daily challenge (first name + surname initial only; resets every Monday).
export const Leaderboard = ({ refresh, teacherAuth }) => {
  const { L } = useLang();
  const student = isStudent();
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!student && !teacherAuth) return;
    const req = teacherAuth ? api.get("/teacher/smart/leaderboard", teacherAuth) : api.get("/student/smart/leaderboard", authHeader("student"));
    req.then((r) => setD(r.data)).catch(() => setD(false));
  }, [refresh, teacherAuth, student]);
  if (!student && !teacherAuth) {
    return (
      <div className="rounded-3xl bg-white border border-slate-200 p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid="leaderboard-guest">
        <div className="flex items-center gap-3"><Trophy className="w-8 h-8 text-[#F5B700]" /><div><div className="font-extrabold text-[#0A192F]">{L("Weekly school leaderboard", "साप्ताहिक स्कूल लीडरबोर्ड")}</div>
          <p className="text-sm text-slate-500">{L("Log in as a student to compete with classmates from your school.", "अपने स्कूल के साथियों से मुक़ाबले के लिए छात्र लॉगिन करें।")}</p></div></div>
        <Link to="/student/login" className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-[#0A192F] text-white text-sm font-bold" data-testid="leaderboard-login-btn"><LogIn className="w-4 h-4" />{L("Log in", "लॉग इन")}</Link>
      </div>
    );
  }
  if (d === false) return <p className="text-sm text-[#C81E4B]" data-testid="leaderboard-error">{L("Couldn't load the leaderboard.", "लीडरबोर्ड लोड नहीं हुआ।")}</p>;
  if (!d) return <div className="h-40 rounded-3xl bg-white/70 animate-pulse" data-testid="leaderboard-loading" />;
  const meOutside = d.me && !d.top.some((r) => r.me);
  return (
    <div className="rounded-3xl bg-[#F7FAFF] border border-slate-200 p-5 sm:p-6" data-testid="leaderboard">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div><div className="font-extrabold text-lg text-[#0A192F] inline-flex items-center gap-2"><Trophy className="w-5 h-5 text-[#F5B700]" />{L("This week's leaderboard", "इस हफ़्ते का लीडरबोर्ड")}</div>
          <div className="text-xs text-slate-500" data-testid="leaderboard-week">{d.school_name} · {day(d.week_start)} – {day(d.week_end)} · {L("resets every Monday", "हर सोमवार रीसेट")}</div></div>
        <div className="text-[11px] text-slate-500">{L("10 points per correct answer + 5 for finishing the day", "हर सही जवाब 10 पॉइंट + दिन पूरा करने पर 5")}</div>
      </div>
      {d.top.length === 0 ? <p className="mt-4 text-sm text-slate-600" data-testid="leaderboard-empty">{L("No points yet this week. Be the first!", "इस हफ़्ते अभी कोई पॉइंट नहीं। सबसे पहले आप बनिए!")}</p> : (
        <ol className="mt-4 space-y-2">{d.top.map((r, i) => <Row key={i} r={r} i={i} />)}</ol>
      )}
      {meOutside && <ol className="mt-3 pt-3 border-t border-dashed border-slate-300"><Row r={d.me} i="me" /></ol>}
    </div>
  );
};
